import * as tf from '@tensorflow/tfjs';

let EPSILON = null

export function flattenQueue(queue) {
    const frameSize = queue[0].length;
    const freqData = new Float32Array(queue.length * frameSize);
    queue.forEach((data, i) => {freqData.set(data, i * frameSize)});
    return freqData;
}

export function getInputTensorFromFrequencyData(freqData, shape) {
    const vals = new Float32Array(tf.util.sizeFromShape(shape));
    // If the data is less than the output shape, the rest is padded with zeros.
    vals.set(freqData, vals.length - freqData.length);
    return tf.tensor(vals, shape);
}

export function normalize(x) {
    if (EPSILON == null) {
      EPSILON = tf.backend().epsilon();
    }
    /* return tf.tidy(() => {
        const {mean, variance} = tf.moments(x);
        // Add an EPSILON to the denominator to prevent division-by-zero.
        return tf.div(tf.sub(x, mean), tf.add(tf.sqrt(variance), EPSILON));
    }); */

    const {mean, variance} = tf.moments(x);
    const subTensor = tf.sub(x, mean)
    const addTesnor = tf.add(tf.sqrt(variance), EPSILON)
    const divTesnor = tf.div(subTensor, addTesnor)
    subTensor.dispose()
    addTesnor.dispose()
    mean.dispose()
    variance.dispose()
    return divTesnor;
}

export function normalizeX (queue) {
    const shape = [1, 43, 232, 1]
    const freqData = flattenQueue(queue)
    const freqDataTensor = getInputTensorFromFrequencyData(freqData, shape);
    const normalizedX = normalize(freqDataTensor);
    const normalizedXdata = normalizedX.dataSync();
    freqDataTensor.dispose()
    normalizedX.dispose()
    return normalizedXdata
}

function toByte (freqDataFloat) {
    const dbMin = -120;
    const dbMax = -30;
    const columnTruncateLength = 232;
    return freqDataFloat.map(value => 255 * ((value - dbMin) / (dbMax - dbMin))).slice(0, columnTruncateLength);
}

export default class Audio {
    constructor() {
        this.deviceId = null;
        this.devices = null;
        this.device = null;
        this.mediaStream = null;
        this.permission = true;

        this.fftSize = 1024;
        this.fftSize = 1024
        this.frameSize = 43;
        this.columnTruncateLength = 232;
        this.sampleRateHz = 44100;
        this.frameDurationSec = this.fftSize / this.sampleRateHz;
        this.numFramesPerSpectrogram = this.frameSize // Math.ceil(durationSec / frameDurationSec);
        this.dbMax = -30;
        this.dbMin = -100; //120 /* const dbMin = this.analyser.minDecibels */
        this.interval = 1000 / this.numFramesPerSpectrogram;
        
        const devicechangeEvt = async () => {
            this.getDevices().then(devices => {
                if (devices.length === 0) {
                    this.deviceId = null;
                } else {
                    const isDisconnect = ! devices.find((device) => {
                        return device.deviceId === this.deviceId;
                    })
                    if(isDisconnect) {
                        this.start()
                    }
                }
            })
        }

        navigator.mediaDevices.addEventListener('devicechange', devicechangeEvt);
        navigator.permissions.query({ name: 'microphone' }).then(function(permissionStatus) {
            permissionStatus.onchange = function() {
                devicechangeEvt()
            };
        }).catch(function(error) {
            console.error('Error checking audio permission:', error);
        });
    }

    createAnalyser(stream, sampleRateHz, fftSize){
        const actx = new AudioContext({
            sampleRate: sampleRateHz,
        });
    
        const analyser = actx.createAnalyser();
        analyser.fftSize = fftSize * 2;
        analyser.smoothingTimeConstant = 0;


        const source = actx.createMediaStreamSource(stream)
        source.connect(analyser);

        return analyser
    }

    static normalizeX(...args){
        return normalizeX(...args)
    }

    async set(deviceId){
        try {
            this.stop();
            const mediaStream = await navigator.mediaDevices.getUserMedia({audio: {deviceId: deviceId}})
            this.analyser = this.createAnalyser(mediaStream, this.sampleRateHz, this.fftSize);
            this.mediaStream = mediaStream;
        } finally {
            this.deviceId = deviceId
        }
    }

    update(){
        if (this.analyser) {
            const freqDataFloat = new Float32Array(this.fftSize);
            this.analyser.getFloatFrequencyData(freqDataFloat);
            return freqDataFloat;
        } else {
            return null
        }
    }

    stop(){
        if (this.mediaStream) {
            const tracks = this.mediaStream.getTracks();
            tracks.forEach((track) => {
                track.stop();
            });
            this.mediaStream = null;
        }
    }

    resume(){
        this.set(this.deviceId)
    }

    async listen(callback){
        if (!this.mediaStream) {
            await this.start();
        }

        const loop = () => {
            const freqDataFloat = this.update();
            
            if (freqDataFloat && freqDataFloat[0] !== -Infinity) {
                callback(toByte(freqDataFloat), freqDataFloat)
            }

            setTimeout(loop, this.interval)
        }

        setTimeout(loop, 1000 * (this.fftSize / this.sampleRateHz) * 1.5)
    }

    async record(callback, endCallback) {
        if (!this.mediaStream) {
            await this.start();
        }

        const chunks = [];
        const queue = [];

        const mediaRecorder = new MediaRecorder(this.mediaStream);

        mediaRecorder.ondataavailable = (event) => {
            chunks.push(event.data);
        }

        mediaRecorder.onstop = (event) => {
            endCallback(queue, chunks)
        }

        const loop = () => {
            const freqDataFloat = this.update();
            
            if (freqDataFloat[0] !== -Infinity) {
                const freqDataByte = Array.from(toByte(freqDataFloat))
                queue.push(freqDataByte)
                callback(freqDataByte)
            }
            if (queue.length >= 43) {
                mediaRecorder.stop();
            } else {
                setTimeout(loop, this.interval)
            }
        }

        mediaRecorder.start();
        loop()
    }

    async getDevices(){
        let audioDevices = null
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            audioDevices = devices.filter(function(device) {
                return device.kind === 'audioinput' && device.deviceId !== 'default' && device.deviceId != 'communications';
            });
        } finally {
            this.devices = audioDevices
        } 
        
        return audioDevices
    }

    async start() {
        try {
            const devices = await this.getDevices()
            if (devices.length > 0 && devices[0].deviceId.length > 0) {
                await this.set(devices[0].deviceId)
                this.permission = true;
            } else {
                throw new Error('No Device Detected');
            } 
        } catch (error) {
            if (error.message == 'Permission denied') {
                console.log('Permission denied')
                this.permission = false
            }
            if (error.message == 'No Device Detected') {
                //this.permission = false
            }
            setTimeout(() => {
                this.start()
            })
        }
    }
}